import { useEffect, useState } from 'react'

import { useMutation } from '@apollo/client/react'

import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { graphql } from '../../../gql/gql'
import type { ObPlanId, BillingInterval } from '../../../gql/graphql'
import { useOrganizationId } from '../../../hooks/useCurrentOrganization'
import { redirectToUrl } from '../../../common/utils/urls'

type ErrorState =
  | {
      message: string
    }
  | Error
  | null

export type CheckoutSessionParams = {
  planName: ObPlanId
  interval: BillingInterval
  quantity: number
  cta?: string
  upgradePathName?: string
  initialQuantity?: number
  initialSelectedPlan?: string
  initialInterval?: string
}

export type UseCheckoutSessionResponse = {
  checkoutSession: (params: CheckoutSessionParams) => Promise<void>
  error: ErrorState
  loading: boolean
}

const CREATE_CHECKOUT_SESSION = graphql(/* GraphQL */ `
  mutation createCheckoutSession($input: CreateCheckoutSessionInput!) {
    createCheckoutSession(input: $input) {
      ... on CreateCheckoutSessionResponse {
        redirectUrl
      }
      ... on BillingError {
        userFriendlyMessage
      }
    }
  }
`)

function prefixWithProduct(cta?: string): string | undefined {
  if (!cta) return undefined
  return `publish-${cta}`
}

function formatCTAString(value: string): string {
  return value.replace(/\s+/g, '-').toLowerCase()
}

function useCheckoutSession(): UseCheckoutSessionResponse {
  const organizationId = useOrganizationId()
  const [createCheckoutSession, { data, error: mutationError, loading }] =
    useMutation(CREATE_CHECKOUT_SESSION)
  const [error, setError] = useState<ErrorState>(null)

  async function checkoutSession(params: CheckoutSessionParams): Promise<void> {
    const {
      planName,
      interval,
      quantity,
      cta,
      upgradePathName,
      initialQuantity,
      initialSelectedPlan,
      initialInterval,
    } = params

    if (!organizationId || loading) {
      return
    }

    if (organizationId) {
      const result = await createCheckoutSession({
        variables: {
          input: {
            organizationId,
            plan: planName,
            interval,
            quantity,
            redirectUrl: window.location.href,
            attribution: {
              cta: prefixWithProduct(cta),
              upgradePathName,
            },
          },
        },
      })

      // Track checkout session creation immediately if successful
      if (
        result?.data?.createCheckoutSession?.__typename ===
        'CreateCheckoutSessionResponse'
      ) {
        BufferTracker.stripeCheckoutSessionCreated({
          organizationId: organizationId ?? '',
          selectedPlan: formatCTAString(`${planName} ${interval}`),
          channelQuantity: quantity,
          isStripeCheckout: true,
          cta,
          initialChannelQuantity: initialQuantity,
          initialSelectedPlan,
          initialCycle: initialInterval,
          clientName: Client.PublishWeb,
        })
      }
    }
  }

  useEffect(() => {
    if (mutationError) {
      setError(mutationError)
    }

    if (data?.createCheckoutSession) {
      const response = data.createCheckoutSession

      // Handle error case
      if (response.__typename === 'BillingError') {
        setError({
          message: response.userFriendlyMessage,
        })
        return
      }

      // Handle success case
      if (
        response.__typename === 'CreateCheckoutSessionResponse' &&
        response.redirectUrl
      ) {
        // Track checkout session creation
        // Tracking is now handled directly in checkoutSession function

        redirectToUrl(response.redirectUrl)
      }
    }
  }, [mutationError, data, organizationId])

  return {
    checkoutSession,
    error,
    loading,
  }
}

export default useCheckoutSession
